import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import type { CRB } from '@gcv/shared';
import { useMediaQuery, useTheme } from '@mui/material';

import { CrbmApi } from 'api';
import type { Column, Row } from 'ui';
import {
  Button,
  Container,
  ContainerItem,
  Dialog,
  InputCheckBox,
  Table,
  TableContainer,
  ZeroState
} from 'ui';
import { Spinner, SubTitle, Text } from 'ui/atoms';
import palette from '../../theme/palette';

interface BusinessesModalProps {
  isOpen: boolean;
  onSubmit: (selectedBusiness?: string, businessNotFound?: boolean) => void;
  onClose: () => void;
  onCancel: () => void;
  dispensary: CRB;
}

interface BusinessTableData {
  id: number;
  businessName: string;
  match_quality: number;
}

export const BusinessesModal: React.FC<BusinessesModalProps> = (props) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'md'));

  const { isOpen, onSubmit, onClose, onCancel, dispensary } = props;

  const crbmApi = new CrbmApi();

  const [selectedBusiness, setSelectedBusiness] = useState('');
  const [businessNotFound, setBusinessNotFound] = useState<boolean>(false);
  const [loadingBusinesses, setLoadingBusinesses] = useState(false);

  const [availableBusinesses, setAvailableBusinesses] = useState<BusinessTableData[]>([]);

  const form = useForm();

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    setLoadingBusinesses(true);

    crbmApi
      .searchBusinesses(dispensary.id)
      .then((res) => {
        if (res.length === 0) {
          setBusinessNotFound(true);
        } else {
          const businesses = res
            .map((b) => {
              return {
                id: b.id,
                businessName: b.name ?? '--',
                match_quality: b.match_quality
              };
            })
            .slice(0, 5);

          setAvailableBusinesses(businesses);
        }
      })
      .catch((e) => {
        console.log(e.message);
      })
      .finally(() => {
        setLoadingBusinesses(false);
      });
  }, [isOpen]);

  const businessColumns: Column<BusinessTableData>[] = [
    {
      id: 'businessName',
      label: 'Business Name',
      widthPercent: 70,
      format: (row) => (row.businessName === '' ? '--' : row.businessName)
    },
    {
      id: 'match_quality',
      label: 'Match Quality',
      widthPercent: 30,
      format: (row) => (row.match_quality.toString() === '' ? '--' : row.match_quality.toFixed(1) + '%')
    }
  ];

  const businessRows: Row<BusinessTableData>[] = availableBusinesses.map((business) => {
    return {
      id: business.id.toString(),
      disableSelect: businessNotFound,
      data: {
        ...business
      }
    };
  });

  return (
    <Dialog
      action={
        <Button
          color="primary"
          disabled={loadingBusinesses || (!selectedBusiness && !businessNotFound)}
          label="Continue"
          onClick={() => onSubmit(selectedBusiness, businessNotFound)}
          style={{ minWidth: isMobile ? '100%' : '100px' }}
          dataCy="continue-business-name-button"
        />
      }
      cancelButtonText="Back"
      onCancel={onCancel}
      handleClose={onClose}
      height={isMobile ? '400px' : '496px'}
      isOpen={isOpen}
      title="Marijuana Businesses"
      width={isMobile ? '360px' : isTablet ? '85vw' : '898px'}
    >
      {loadingBusinesses ? (
        <Container column data-cy="loading-businesses-container" padding={0} width="100%">
          <ContainerItem justify="center" width="100%">
            <Spinner />
            <br />
            <Text
              sx={
                isMobile
                  ? {
                      fontSize: '14px',
                      fontWeight: '500',
                      lineHeight: '20px',
                      color: palette.background.disabled
                    }
                  : {}
              }
              content="Checking business data..."
            />
            {isMobile ? (
              <Text
                sx={
                  isMobile
                    ? {
                        fontSize: '14px',
                        fontWeight: '500',
                        lineHeight: '20px',
                        color: palette.background.disabled
                      }
                    : {}
                }
                content="(This could take up to one minute.)"
              />
            ) : (
              <SubTitle content="(This could take up to one minute.)" />
            )}
          </ContainerItem>
        </Container>
      ) : (
        <Container column data-cy="verify-license-container" padding={0} width="100%">
          <ContainerItem justify="left" padding={isMobile ? '0' : '0 8px 16px 8px'} width="100%">
            <Text
              sx={isMobile ? { fontSize: '14px', fontWeight: '500', lineHeight: '20px' } : {}}
              content="We’ve found the following matches, please choose your Marijuana Business."
            />
          </ContainerItem>

          <ContainerItem padding="8px 4px 8px 0" width="100%">
            <TableContainer>
              <Table
                minWidth={isMobile ? 160 : 590}
                clickable
                noPagination
                singleSelectable
                columns={businessColumns}
                defaultSort="match_quality"
                defaultSortDirection="desc"
                noDataComponent={() => (
                  <ZeroState
                    noBorder
                    titleFont="medium"
                    title="Oops, we weren't able to find your business with the name provided"
                    icon={'/img/empty-states/no-results.svg'}
                  />
                )}
                returnSelection={(selected) => {
                  setSelectedBusiness(selected[0]);
                }}
                rows={businessRows}
              />
            </TableContainer>
          </ContainerItem>

          <ContainerItem width="100%">
            <InputCheckBox
              {...form}
              label="I don’t see my marijuana business"
              name="businessNotFound"
              readOnly={availableBusinesses.length === 0}
              checked={businessNotFound}
              onChange={(e, checked) => {
                setBusinessNotFound(checked);
              }}
            />
          </ContainerItem>
        </Container>
      )}
    </Dialog>
  );
};
